import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/content`);
      const contentMap = {};
      response.data.forEach(item => {
        contentMap[item.section_key] = item.content;
      });
      setContent(contentMap);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Contact persons with combined phone + WhatsApp
  const contactPersons = [
    {
      name: content.contact_person_1_name || 'Contact Person 1',
      phone: content.contact_person_1_phone || '+91 98765 43210',
      whatsapp: content.contact_person_1_whatsapp || '919876543210'
    },
    {
      name: content.contact_person_2_name || 'Contact Person 2',
      phone: content.contact_person_2_phone || '+91 98765 43211',
      whatsapp: content.contact_person_2_whatsapp || '919876543211'
    }
  ];

  return (
    <div className="pt-16">
      <section className="bg-gradient-to-br from-white via-purple-50/30 to-white py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight text-gray-900 mb-6">
              Contact <span className="text-brand-purple">Us</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-gray-600 font-body max-w-3xl mx-auto">
              Have questions? We're here to help. Reach out to us anytime.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-semibold text-gray-900 mb-8">
                Get in Touch
              </h2>

              <div className="space-y-6">
                {/* Contact Person Cards */}
                {contactPersons.map((person, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all"
                    data-testid={`contact-person-${index + 1}`}
                  >
                    <h3 className="font-heading text-xl font-semibold text-gray-900 mb-4">
                      {person.name}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {/* Phone Button */}
                      <a
                        href={`tel:${person.phone.replace(/\s/g, '')}`}
                        className="flex items-center gap-2 bg-brand-purple/10 text-brand-purple px-4 py-2 rounded-full hover:bg-brand-purple hover:text-white transition-all font-body"
                        data-testid={`contact-phone-${index + 1}`}
                      >
                        <Phone size={18} />
                        <span>{person.phone}</span>
                      </a>
                      {/* WhatsApp Button */}
                      <a
                        href={`https://wa.me/${person.whatsapp}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-green-100 text-green-700 px-4 py-2 rounded-full hover:bg-green-600 hover:text-white transition-all font-body"
                        data-testid={`contact-whatsapp-${index + 1}`}
                      >
                        <MessageCircle size={18} />
                        <span>WhatsApp</span>
                      </a>
                    </div>
                  </motion.div>
                ))}

                {/* Email Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all"
                  data-testid="contact-email-card"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="text-brand-orange" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold text-gray-900 mb-1">
                        Email
                      </h3>
                      <a
                        href={`mailto:${content.contact_email || 'info@indwellerstay.com'}`}
                        className="text-brand-purple hover:text-brand-orange transition-colors font-body"
                        data-testid="contact-email-link"
                      >
                        {content.contact_email || 'info@indwellerstay.com'}
                      </a>
                    </div>
                  </div>
                </motion.div>

                {/* Address Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  viewport={{ once: true }}
                  className="bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all"
                  data-testid="contact-address-card"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-purple/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-brand-purple" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-semibold text-gray-900 mb-1">
                        Address
                      </h3>
                      <p className="text-gray-600 font-body">
                        {content.contact_address || 'Andheri East, Mumbai, Maharashtra 400069, India'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>

              <div className="mt-8 bg-gradient-to-r from-brand-purple to-purple-900 p-8 rounded-2xl text-white">
                <h3 className="font-heading text-2xl font-semibold mb-4">
                  Quick Inquiry
                </h3>
                <p className="font-body mb-6 opacity-90">
                  Need immediate assistance? Fill out our inquiry form and we'll get back to you within 24 hours.
                </p>
                <Link
                  to="/inquiry"
                  data-testid="contact-inquiry-button"
                  className="inline-block bg-brand-orange text-white px-8 py-3 rounded-full font-body font-medium hover:bg-opacity-90 transition-all"
                >
                  Submit Inquiry
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-semibold text-gray-900 mb-8">
                Visit Our Office
              </h2>

              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100" data-testid="contact-map">
                <div className="w-full h-96">
                  <iframe
                    src={content.contact_map_embed || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d241317.11609823277!2d72.74109995709657!3d19.082177568676286!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c6306644edc1%3A0x5da4ed8f8d648c69!2sMumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Office Location"
                  ></iframe>
                </div>
              </div>

            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-12 rounded-2xl border border-gray-100 text-center"
            data-testid="contact-social"
          >
            <h2 className="font-heading text-3xl font-semibold text-gray-900 mb-4">
              Follow Us
            </h2>
            <p className="text-gray-600 font-body mb-6">
              Stay updated with our latest properties and offers
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-brand-purple/10 rounded-full flex items-center justify-center hover:bg-brand-purple hover:text-white transition-all"
                aria-label="Facebook"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-brand-purple/10 rounded-full flex items-center justify-center hover:bg-brand-purple hover:text-white transition-all"
                aria-label="Instagram"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-brand-purple/10 rounded-full flex items-center justify-center hover:bg-brand-purple hover:text-white transition-all"
                aria-label="Twitter"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                </svg>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
